import { useMemo, useState } from "react";
import { Text } from "react-native-paper";
import { useAppTheme } from "../useAppTheme";
import Box from "./Box";
import { TextSkeleton } from "./ChatUI/TextSkeleton";
import HintAccordionListItem from "./HintAccordionListItem";
import RenderMarkdown from "./MarkdownRenderer";

export type HintAccordionProps = {
	hint?: string;
	loading?: boolean;
	streaming?: boolean;
	title?: string;
	defaultExpanded?: boolean;
	expanded?: boolean;
	onToggle?: () => void;
	actionContent?: React.ReactNode;
};

const HintAccordion = ({
	hint,
	loading,
	streaming,
	title = "Show Hint",
	defaultExpanded = false,
	expanded,
	onToggle,
	actionContent,
}: HintAccordionProps) => {
	const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
	const theme = useAppTheme();
	const showHint = expanded ?? internalExpanded;
	const color = showHint ? theme.colors.text : theme.colors.primary;

	const handleToggle = () => {
		if (expanded == null) {
			setInternalExpanded((previous) => !previous);
		}
		onToggle?.();
	};

	const accordionTitle = useMemo(() => {
		if (!showHint) {
			return (
				<Text
					variant="bodyMedium"
					style={{
						fontWeight: "bold",
						color,
					}}
				>
					{title}
				</Text>
			);
		}

		if (loading || !hint) {
			return <TextSkeleton height={14} numLines={1} />;
		}

		return (
			<Box flexDirection="row" justifyContent="space-between">
				<Box flex={1}>
					<RenderMarkdown
						text={hint}
						variant="answer"
						customStyles={{
							text: {
								fontSize: 16,
								lineHeight: 24,
								color,
							},
						}}
					/>
				</Box>
				{actionContent ? (
					<Box paddingHorizontal={8}>{actionContent}</Box>
				) : null}
			</Box>
		);
	}, [actionContent, color, hint, loading, showHint, title]);

	return (
		<HintAccordionListItem
			showHint={showHint}
			title={accordionTitle}
			handleToggle={handleToggle}
			isLastHint={Boolean(hint?.length) || streaming}
		/>
	);
};

export default HintAccordion;
